import { createAsyncCache } from '@nzyme/utils/createAsyncCache.js';
import debounce from 'lodash.debounce';
import { reactive } from 'vue';
/**
 * Creates a batched cache with automatic request batching and optional expiration.
 *
 * Features:
 * - Batches multiple requests within a debounce window into a single fetch call
 * - Caches results with customizable cache keys
 * - Optional automatic expiration of cached entries
 * - Deduplicates concurrent requests for the same parameter
 *
 * @template TParam - The parameter type for fetching items
 * @template TValue - The cached value type
 * @template TCacheKey - The cache key type (defaults to TParam)
 * @param options - Configuration options
 * @returns A batched cache interface
 *
 * @example
 * ```ts
 * // Simple cache with ID as both parameter and cache key
 * const cache = createBatchedCache({
 *   fetchBatch: async (ids) => apiClient.FileGet({ fileIds: ids }),
 *   matchParam: (file, id) => file.id === id,
 * });
 *
 * // Complex cache with composite parameters
 * const cache = createBatchedCache({
 *   getCacheKey: ({ consultationId, fileId }) => `${consultationId}-${fileId}`,
 *   fetchBatch: async (params) => {
 *     const groups = groupBy(params, p => p.consultationId);
 *     const results = await Promise.all(
 *       Object.entries(groups).map(([consultationId, items]) =>
 *         apiClient.FileGet({
 *           consultationId,
 *           fileIds: items.map(i => i.fileId)
 *         })
 *       )
 *     );
 *     return results.flat();
 *   },
 *   matchParam: (file, param) => file.id === param.fileId,
 *   isExpired: (file, now) => file.expiresAt && now >= file.expiresAt - 300000,
 * });
 * ```
 */
export function createBatchedCache(options) {
    const { getCacheKey = (param) => param, fetchBatch, matchParam, isExpired, debounceMs = 100, expiryCheckIntervalMs = 60_000, } = options;
    // Reactive cache map
    const cacheMap = reactive(new Map());
    // Reactive cache for values
    const cache = createAsyncCache({
        cache: cacheMap,
        cacheKey: getCacheKey,
        getValue: (param) => fetchWithBatching(param),
    });
    // Pending requests awaiting batch flush
    const pendingQueue = new Map();
    // In-flight requests currently being fetched
    const inFlightMap = new Map();
    // Debounced flush function
    const debouncedFlush = debounce(flush, debounceMs);
    // Set up automatic expiration checking if isExpired is provided
    if (isExpired) {
        setInterval(checkExpiredEntries, expiryCheckIntervalMs);
    }
    return {
        get: (param) => cache.get(param),
        getCached: (param) => cache.getCached(param),
        set: (param, value) => cache.set(param, value),
        clear: () => {
            cache.clear();
            pendingQueue.clear();
        },
        delete: (param) => cache.delete(param),
    };
    /**
     * Fetches a value with batching support.
     * Queues the request and triggers a debounced flush.
     */
    function fetchWithBatching(param) {
        const key = getCacheKey(param);
        // Check if already in-flight
        const inFlight = inFlightMap.get(key);
        if (inFlight) {
            return inFlight;
        }
        // Get or create pending queue for this key
        let queue = pendingQueue.get(key);
        if (!queue) {
            queue = [];
            pendingQueue.set(key, queue);
        }
        // Create new promise with external resolve/reject
        const promise = new Promise((resolve, reject) => {
            queue.push({ resolve, reject, param });
        });
        // Trigger debounced flush
        void debouncedFlush();
        return promise;
    }
    /**
     * Flushes the pending queue by making a batched fetch call.
     * Captures current queue and clears it so new requests go to a fresh queue.
     */
    async function flush() {
        // Capture the current pending queue and clear it
        const batchQueue = new Map(pendingQueue);
        pendingQueue.clear();
        if (batchQueue.size === 0) {
            return;
        }
        // Extract all parameters for the batch
        const params = [];
        const requestMap = new Map();
        for (const [key, requests] of batchQueue) {
            // Get param from the first request (all requests for same key have same param)
            const firstRequest = requests[0];
            if (firstRequest) {
                params.push(firstRequest.param);
                requestMap.set(key, requests);
            }
        }
        // Create a shared promise for this batch
        const batchPromise = (async () => {
            try {
                // Make single fetch call for all parameters
                const results = await fetchBatch(params);
                // Distribute results to waiting resolvers
                for (const [key, requests] of requestMap) {
                    const firstRequest = requests[0];
                    if (!firstRequest) {
                        continue;
                    }
                    const param = firstRequest.param;
                    // Find matching value for this parameter
                    const value = results.find(result => matchParam(result, param));
                    if (value) {
                        // Add to cache
                        cache.set(param, value);
                        // Resolve all promises
                        for (const request of requests) {
                            request.resolve(value);
                        }
                    }
                    else {
                        // Value not found in response
                        const error = new Error(`Value not found for key: ${String(key)}`);
                        for (const request of requests) {
                            request.reject(error);
                        }
                    }
                    // Remove from in-flight map
                    inFlightMap.delete(key);
                }
            }
            catch (error) {
                // Reject all waiting promises
                for (const [key, requests] of requestMap) {
                    for (const request of requests) {
                        request.reject(error);
                    }
                    inFlightMap.delete(key);
                }
            }
        })();
        // Add all keys to in-flight map
        for (const [key, requests] of requestMap) {
            const firstRequest = requests[0];
            if (!firstRequest) {
                continue;
            }
            const param = firstRequest.param;
            inFlightMap.set(key, batchPromise.then(() => {
                const value = cache.getCached(param);
                if (!value) {
                    throw new Error(`Value not found in cache for key: ${String(key)}`);
                }
                return value;
            }));
        }
        await batchPromise;
    }
    /**
     * Checks for expired cache entries and removes them.
     * Only runs if isExpired function is provided.
     */
    function checkExpiredEntries() {
        if (!isExpired) {
            return;
        }
        const now = Date.now();
        // Iterate through cache and check expiration
        const keysToDelete = [];
        for (const [key, value] of cacheMap) {
            if (isExpired(value, now)) {
                keysToDelete.push(key);
            }
        }
        for (const key of keysToDelete) {
            cacheMap.delete(key);
        }
    }
}
//# sourceMappingURL=createBatchedCache.js.map